import { cloneDeep } from 'lodash';
import { getPaceZone, paceKm2Mile } from '@client/helper/pace';
import { getHrZone } from '@client/helper/hr';
import Definition from '@client/config/definition';
import { getCyclePowerZone } from '@client/pages/personalSetting/components/defaultsCyclePower';
import * as corosSportConfig from '@coros/sport-config';

const { getGradingSytemOptions } = corosSportConfig.sport.climb;

const optionsAnalyse = (type = '', config = {}, targetConfig, allIntensityConfig) => {
  const typeConfig = cloneDeep(config.typeConfig || {});
  const options = cloneDeep(config.options || []);

  const deal = (options) => {
    const result = [];
    options.forEach((item) => {
      if (Array.isArray(item)) {
        const children = deal(item);

        if (!children.length) {
          return;
        }

        result.push({
          children
        });
        return;
      }

      if (!typeConfig?.[item]) {
        if (type === 'target') {
          typeConfig[item] = targetConfig?.[item];
        } else if (type === 'intensity') {
          typeConfig[item] = allIntensityConfig?.[item];
        }
      }

      if (!typeConfig[item]) {
        return;
      }

      result.push({
        key: item,
        nameText: typeConfig[item]?.nameText
      });
    });
    return result;
  };

  const newOptions = deal(options);

  return {
    typeConfig: cloneDeep(typeConfig),
    options: newOptions
  };
};

const exerciseConfigAnalyse = (config, targetConfig, allIntensityConfig) => {
  const result = cloneDeep(config);

  for (const exerciseType in result) {
    for (const type in result[exerciseType]) {
      result[exerciseType][type] = Object.assign(
        {},
        result[exerciseType][type],
        optionsAnalyse(type, result[exerciseType][type], targetConfig, allIntensityConfig)
      );
    }
  }
  return result;
};

const parsePowerZoneList = (cp) => {
  cp = cp || 0;

  const percentage = [
    [65, 80],
    [81, 90],
    [91, 100],
    [101, 115],
    [116, '']
    // [116, 300]
  ];

  function getNum(percentage) {
    if ([undefined, null, ''].includes(percentage)) {
      return '';
    }
    const multiple = 100;
    let result = Math.round((percentage * cp) / multiple);
    result = Math.round(result / 10);
    return result * 10;
  }

  const result = [];
  for (let n = 0, length = percentage.length; n < length; n++) {
    const item = percentage[n];
    const range = [getNum(item[0]), getNum(item[1])];

    const newItem = {
      id: n + 1,
      ratioRange: item,
      ratioRangeText: `${item[0]}%-${item[1]}%`,
      range,
      rangeText: `${range[0]}-${range[1]}`
    };

    if ([undefined, null, ''].includes(item[1])) {
      newItem.ratioRangeText = `>${item[0]}%`;
      newItem.rangeText = `>${range[0]}`;
    }

    result.push(newItem);
  }
  return result;
};

const parseTargetConfig = (t, unit) => {
  return {
    // 不设置
    notSet: {
      nameText: t('S4313'),
      type: 'notSet'
    },
    // 自由模式
    manualEnd: {
      nameText: t('S4108'),
      type: 'manualEnd'
    },
    // 时间
    time: {
      nameText: t('S1735'),
      type: 'time',
      unitConfig: {
        default: {
          typeConfig: null,
          inputConfig: {
            max: 86399,
            min: 0,
            defaultValue: 300
          }
        }
      },
      defaultUnit: 'default'
    },
    // 次数
    count: {
      nameText: t('S4107'),
      type: 'count',
      unitConfig: {
        default: {
          typeConfig: null,
          inputConfig: {
            max: 500,
            min: 1,
            precision: 0,
            step: 1,
            defaultValue: 15
          }
        }
      },
      defaultUnit: 'default'
    },
    // 距离
    distance: {
      nameText: t('S2657'),
      type: 'distance',
      unitConfig: {
        km: {
          typeConfig: null,
          inputConfig: {
            max: 499.99,
            min: 0.1,
            precision: 2,
            step: 0.01,
            defaultValue: 1
          }
        },
        mi: {
          typeConfig: null,
          inputConfig: {
            max: 499.99,
            min: 0.1,
            precision: 2,
            step: 0.01,
            defaultValue: 1
          }
        },
        m: {
          typeConfig: null,
          inputConfig: {
            max: 1000,
            min: 10,
            precision: 0,
            step: 10,
            defaultValue: 100
          }
        },
        yd: {
          typeConfig: null,
          inputConfig: {
            max: 1000,
            min: 10,
            precision: 0,
            step: 10,
            defaultValue: 100
          }
        }
      },
      // defaultUnit: 'km'
      defaultUnit: {
        [Definition.METRIC_UNITS]: 'km',
        [Definition.IMPERIAL_UNITS]: 'mi'
      }[unit]
    },
    // 负荷
    load: {
      nameText: t('S4491'),
      type: 'load',
      unitConfig: {
        default: {
          typeConfig: null,
          inputConfig: {
            max: 999,
            min: 0,
            precision: 0,
            step: 1,
            defaultValue: 100
          }
        }
      },
      defaultUnit: 'default'
    },
    // 心率恢复
    heartRateRecovery: {
      nameText: t('S4112'),
      type: 'heartRateRecovery',
      unitConfig: {
        bpm: {
          typeConfig: null,
          inputConfig: {
            max: 180,
            min: 30,
            precision: 0,
            step: 1,
            defaultValue: 120
          }
        }
      },
      defaultUnit: 'bpm'
    },
    cumulativeClimb: {
      nameText: t('S5534'),
      type: 'cumulativeClimb',
      unitConfig: {
        m: {
          typeConfig: null,
          inputConfig: {
            max: 10000,
            min: 20,
            precision: 0,
            step: 10,
            defaultValue: 500
          }
        },
        ft: {
          typeConfig: null,
          inputConfig: {
            max: 30000,
            min: 20,
            precision: 0,
            step: 10,
            defaultValue: 1000
          }
        }
      },
      // defaultUnit: 'km'
      defaultUnit: {
        [Definition.METRIC_UNITS]: 'm',
        [Definition.IMPERIAL_UNITS]: 'ft'
      }[unit]
    },
    // 线路（攀岩）
    routes: {
      nameText: t('H9039'),
      type: 'routes',
      unitConfig: {
        default: {
          typeConfig: null,
          inputConfig: {
            max: 20,
            min: 1,
            precision: 0,
            step: 1,
            defaultValue: 1
          }
        }
      },
      defaultUnit: 'default'
    }
  };
};

const parseAllIntensityConfig = (data = {}, unit, t, criticalPower) => {
  let zoneData = cloneDeep(data);
  const ltsp = zoneData.ltsp || 0;

  const weightList = [
    // 体重
    {
      type: 1,
      nameText: t('S2305')
    },
    // 负重（自定义）
    {
      type: 0,
      nameText: t('S4299')
    }
  ];

  const heartConfig = {
    ratioRangeConfig: {
      max: 120,
      min: 20,
      precision: 0,
      step: 1,
      defaultValue: [60, 65],
      unit: '%'
    },
    rangeConfig: {
      max: 240,
      min: 30,
      precision: 0,
      step: 1,
      defaultValue: [100, 120],
      unit: 'bpm'
    }
  };

  const powerZoneList = parsePowerZoneList(criticalPower);
  // 注意：与 app/web/projects/client/pages/activity-detail/config/dataCard.js 同步修改
  const powerCustomMap = [
    // 有氧耐力
    {
      type: 1,
      nameText: t('S4343')
    },
    // 有氧动力
    {
      type: 2,
      nameText: t('S4344')
    },
    // 乳酸阈区
    {
      type: 3,
      nameText: t('S4345')
    },
    // 无氧耐力
    {
      type: 4,
      nameText: t('S4346')
    },
    // 无氧动力
    {
      type: 5,
      nameText: t('S4347')
    }
    // {
    //   type: 0,
    //   nameText: t('S4238')
    // }
  ];

  const res = {
    // 不设置
    notSet: {
      nameText: t('S4313'),
      type: 'notSet'
    },
    // 重量
    weight: {
      nameText: '',
      type: 'weight',
      unitConfig: {
        kg: {
          typeConfig: {
            options: weightList,
            // 健身的重量强度默认读取的是动作库的设置
            defaultValue: 1
          },
          inputConfig: {
            max: 300,
            min: 0,
            precision: 1,
            step: 0.5,
            defaultValue: 0
          }
        },
        lbs: {
          typeConfig: {
            options: weightList,
            defaultValue: 1
          },
          inputConfig: {
            max: 300,
            min: 0,
            precision: 1,
            step: 0.5,
            defaultValue: 0
          }
        }
      },
      // defaultUnit: 'kg'
      defaultUnit: {
        [Definition.METRIC_UNITS]: 'kg',
        [Definition.IMPERIAL_UNITS]: 'lbs'
      }[unit]
    },
    // 心率区间
    rhr: {
      nameText: t('S2081'),
      type: 'heart',
      hrType: 'rhr',
      isIntensityPercent: false,
      defaultIntensityCustom: 0,
      unitConfig: {
        default: {
          typeConfig: {
            // options: (getHrZone(zoneData, 2) || []).map((item) => {
            //   item.type = item.id;
            //   item.nameText = t(item.defaultName);
            //   return item;
            // }),
            // defaultValue: 2
          },
          ...heartConfig
        }
      },
      defaultUnit: 'default'
    },
    // 最大心率区间
    '%maxHr': {
      nameText: t('S4499'),
      type: 'heart',
      hrType: 'maxHr',
      isIntensityPercent: true,
      defaultIntensityCustom: 2,
      unitConfig: {
        default: {
          typeConfig: {
            options: (getHrZone(zoneData, 1) || []).map((item) => {
              item.type = item.id;
              item.nameText = t(item.defaultName);
              return item;
            }),
            defaultValue: 2
          },
          ...heartConfig
        }
      },
      defaultUnit: 'default'
    },
    // 储备心率区间
    '%rhr': {
      nameText: t('S4500'),
      type: 'heart',
      hrType: 'rhr',
      isIntensityPercent: true,
      defaultIntensityCustom: 2,
      unitConfig: {
        default: {
          typeConfig: {
            options: (getHrZone(zoneData, 2) || []).map((item) => {
              item.type = item.id;
              item.nameText = t(item.defaultName);
              return item;
            }),
            defaultValue: 2
          },
          ...heartConfig
        }
      },
      defaultUnit: 'default'
    },
    // ftp
    '%ftp': {
      nameText: t('S4654'),
      type: 'ftp',
      isIntensityPercent: true,
      defaultIntensityCustom: 2,
      unitConfig: {
        default: {
          typeConfig: {
            options: getCyclePowerZone('schedule', zoneData),
            defaultValue: 2
          },
          ratioRangeConfig: {
            max: 500,
            min: 40,
            precision: 0,
            step: 1,
            defaultValue: [60, 65],
            unit: '%'
          }
        }
      },
      defaultUnit: 'default'
    },
    // 乳酸阈心率区间
    '%lthr': {
      nameText: t('S4498'),
      type: 'heart',
      hrType: 'lthr',
      isIntensityPercent: true,
      defaultIntensityCustom: 2,
      unitConfig: {
        default: {
          typeConfig: {
            options: (getHrZone(zoneData, 3) || []).map((item) => {
              item.type = item.id;
              item.nameText = t(item.defaultName);
              return item;
            }),
            defaultValue: 2
          },
          ...heartConfig
        }
      },
      defaultUnit: 'default'
    },
    // 配速百分比
    '%thresholdPace': {
      nameText: t('S2063'),
      type: 'pace',
      isIntensityPercent: true,
      defaultIntensityCustom: 2,
      unitConfig: {
        'min/km': {
          typeConfig: {
            options: (getPaceZone(zoneData, ltsp) || []).map((item) => {
              item.nameText = t(item.defaultName);
              return item;
            }),
            defaultValue: 2
          },
          ratioRangeConfig: {
            max: 140,
            min: 20,
            precision: 0,
            step: 1,
            defaultValue: [60, 65],
            unit: '%'
          },
          rangeConfig: {
            max: 1440,
            min: 120,
            precision: 0,
            step: 1,
            defaultValue: [300, 360],
            unit: 'min/km'
          }
        },
        'min/mi': {
          typeConfig: {
            options: (getPaceZone(zoneData, paceKm2Mile(ltsp, 6)) || []).map((item) => {
              item.nameText = t(item.defaultName);
              return item;
            }),
            defaultValue: 2
          },
          ratioRangeConfig: {
            max: 140,
            min: 20,
            precision: 0,
            step: 1,
            defaultValue: [60, 65],
            unit: '%'
          },
          rangeConfig: {
            max: 1440,
            min: 120,
            precision: 0,
            step: 1,
            defaultValue: [300, 360],
            unit: 'min/mi'
          }
        },
        's/100m': {
          typeConfig: {
            options: (getPaceZone(zoneData, ltsp / 10) || []).map((item) => {
              item.nameText = t(item.defaultName);
              return item;
            }),
            defaultValue: 2
          },
          ratioRangeConfig: {
            max: 140,
            min: 20,
            precision: 0,
            step: 1,
            defaultValue: [60, 65],
            unit: '%'
          },
          rangeConfig: {
            max: 150,
            min: 10,
            precision: 0,
            step: 1,
            defaultValue: [40, 41],
            unit: 's/100m'
          }
        }
      },
      // defaultUnit: 'min/km'
      defaultUnit: {
        [Definition.METRIC_UNITS]: 'min/km',
        [Definition.IMPERIAL_UNITS]: 'min/mi'
      }[unit]
    },
    // 配速
    thresholdPace: {
      nameText: t('R6010'),
      type: 'pace',
      isIntensityPercent: false,
      // defaultIntensityCustom: 0,
      unitConfig: {
        'min/km': {
          // typeConfig: {
          //   options: [],
          //   defaultValue: 0
          // },
          ratioRangeConfig: {
            max: 140,
            min: 20,
            precision: 0,
            step: 1,
            defaultValue: [60, 65],
            unit: '%'
          },
          rangeConfig: {
            max: 1440,
            min: 120,
            precision: 0,
            step: 1,
            defaultValue: [300, 360],
            unit: 'min/km'
          }
        },
        'min/mi': {
          // typeConfig: {
          //   options: getPaceZone(zoneData, paceKm2Mile(ltsp, 6)) || [],
          //   defaultValue: 2
          // },
          ratioRangeConfig: {
            max: 140,
            min: 20,
            precision: 0,
            step: 1,
            defaultValue: [60, 65],
            unit: '%'
          },
          rangeConfig: {
            max: 1440,
            min: 120,
            precision: 0,
            step: 1,
            defaultValue: [300, 360],
            unit: 'min/mi'
          }
        },
        's/100m': {
          // typeConfig: {
          //   options: getPaceZone(zoneData, ltsp / 10) || [],
          //   defaultValue: 2
          // },
          ratioRangeConfig: {
            max: 140,
            min: 20,
            precision: 0,
            step: 1,
            defaultValue: [60, 65],
            unit: '%'
          },
          rangeConfig: {
            max: 150,
            min: 10,
            precision: 0,
            step: 1,
            defaultValue: [40, 41],
            unit: 's/100m'
          }
        }
      },
      defaultUnit: {
        [Definition.METRIC_UNITS]: 'min/km',
        [Definition.IMPERIAL_UNITS]: 'min/mi'
      }[unit]
    },
    // 等价配速百分比
    '%adjustedPace': {
      nameText: t('H9031'),
      type: 'adjustedPace',
      pbVersion: 3,
      isIntensityPercent: true,
      defaultIntensityCustom: 2,
      unitConfig: {
        'min/km': {
          typeConfig: {
            options: (getPaceZone(zoneData, ltsp) || []).map((item) => {
              item.nameText = t(item.defaultName);
              return item;
            }),
            defaultValue: 2
          },
          ratioRangeConfig: {
            max: 140,
            min: 20,
            precision: 0,
            step: 1,
            defaultValue: [60, 65],
            unit: '%'
          },
          rangeConfig: {
            max: 1440,
            min: 120,
            precision: 0,
            step: 1,
            defaultValue: [300, 360],
            unit: 'min/km'
          }
        },
        'min/mi': {
          typeConfig: {
            options: (getPaceZone(zoneData, paceKm2Mile(ltsp, 6)) || []).map((item) => {
              item.nameText = t(item.defaultName);
              return item;
            }),
            defaultValue: 2
          },
          ratioRangeConfig: {
            max: 140,
            min: 20,
            precision: 0,
            step: 1,
            defaultValue: [60, 65],
            unit: '%'
          },
          rangeConfig: {
            max: 1440,
            min: 120,
            precision: 0,
            step: 1,
            defaultValue: [300, 360],
            unit: 'min/mi'
          }
        },
        's/100m': {
          typeConfig: {
            options: (getPaceZone(zoneData, ltsp / 10) || []).map((item) => {
              item.nameText = t(item.defaultName);
              return item;
            }),
            defaultValue: 2
          },
          ratioRangeConfig: {
            max: 140,
            min: 20,
            precision: 0,
            step: 1,
            defaultValue: [60, 65],
            unit: '%'
          },
          rangeConfig: {
            max: 150,
            min: 10,
            precision: 0,
            step: 1,
            defaultValue: [40, 41],
            unit: 's/100m'
          }
        }
      },
      defaultUnit: {
        [Definition.METRIC_UNITS]: 'min/km',
        [Definition.IMPERIAL_UNITS]: 'min/mi'
      }[unit]
    },
    // 等价配速
    adjustedPace: {
      nameText: t('H9024'),
      type: 'adjustedPace',
      pbVersion: 3,
      isIntensityPercent: false,
      // defaultIntensityCustom: 0,
      unitConfig: {
        'min/km': {
          // typeConfig: {
          //   options: getPaceZone(zoneData, ltsp) || [],
          //   defaultValue: 2
          // },
          ratioRangeConfig: {
            max: 140,
            min: 20,
            precision: 0,
            step: 1,
            defaultValue: [60, 65],
            unit: '%'
          },
          rangeConfig: {
            max: 1440,
            min: 120,
            precision: 0,
            step: 1,
            defaultValue: [300, 360],
            unit: 'min/km'
          }
        },
        'min/mi': {
          // typeConfig: {
          //   options: getPaceZone(zoneData, paceKm2Mile(ltsp, 6)) || [],
          //   defaultValue: 2
          // },
          ratioRangeConfig: {
            max: 140,
            min: 20,
            precision: 0,
            step: 1,
            defaultValue: [60, 65],
            unit: '%'
          },
          rangeConfig: {
            max: 1440,
            min: 120,
            precision: 0,
            step: 1,
            defaultValue: [300, 360],
            unit: 'min/mi'
          }
        },
        's/100m': {
          // typeConfig: {
          //   options: getPaceZone(zoneData, ltsp / 10) || [],
          //   defaultValue: 2
          // },
          ratioRangeConfig: {
            max: 140,
            min: 20,
            precision: 0,
            step: 1,
            defaultValue: [60, 65],
            unit: '%'
          },
          rangeConfig: {
            max: 150,
            min: 10,
            precision: 0,
            step: 1,
            defaultValue: [40, 41],
            unit: 's/100m'
          }
        }
      },
      defaultUnit: {
        [Definition.METRIC_UNITS]: 'min/km',
        [Definition.IMPERIAL_UNITS]: 'min/mi'
      }[unit]
    },
    // 速度
    speed: {
      nameText: t('S2064'),
      type: 'speed',
      unitConfig: {
        'km/h': {
          typeConfig: null,
          rangeConfig: {
            max: 300,
            min: 0,
            precision: 1,
            step: 0.1,
            defaultValue: [15, 20],
            unit: 'km/h'
          }
        },
        mph: {
          typeConfig: null,
          rangeConfig: {
            max: 300,
            min: 0,
            precision: 1,
            step: 0.1,
            defaultValue: [15, 20],
            unit: 'mph'
          }
        }
      },
      // defaultUnit: 'km/h'
      defaultUnit: {
        [Definition.METRIC_UNITS]: 'km/h',
        [Definition.IMPERIAL_UNITS]: 'mph'
      }[unit]
    },
    // 泳姿
    swimmingStyle: {
      nameText: t('S4113'),
      type: 'swimmingStyle',
      unitConfig: {
        default: {
          typeConfig: {
            options: corosSportConfig.sport.poolSwim.getSwimStyleList(),
            defaultValue: 1
          },
          inputConfig: null
        }
      },
      defaultUnit: 'default'
    },
    // 功率
    power: {
      nameText: t('S3529'),
      type: 'power',
      unitConfig: {
        default: {
          typeConfig: criticalPower
            ? {
                options: powerCustomMap.map((item) => {
                  let powerZone = {};

                  if (item.type > 0) {
                    powerZone = powerZoneList[item.type - 1];
                  }

                  return {
                    ...item,
                    ...powerZone
                  };
                }),
                defaultValue: 1
              }
            : null,
          rangeConfig: {
            max: 2000,
            min: 10,
            precision: 0,
            step: 10,
            defaultValue: [200, 320],
            unit: ''
          }
        }
      },
      defaultUnit: 'default'
    },
    // 踏频（跑步是步频，骑行是踏频）
    cadence: {
      // 步频
      nameText: t('S2651'),
      // 踏频
      // nameText: t('S3528'),
      type: 'cadence',
      unitConfig: {
        default: {
          typeConfig: null,
          rangeConfig: {
            max: 300,
            min: 30,
            precision: 0,
            step: 1,
            defaultValue: [170, 180],
            unit: ''
          }
        }
      },
      defaultUnit: 'default'
    },
    gradeSystem: {
      nameText: t('S4660'),
      type: 'gradeSystem',
      unitConfig: {
        default: {
          typeConfig: {
            options: [],
            defaultValue: 1
          },
          inputConfig: {
            max: 300,
            min: 0,
            precision: 1,
            step: 0.5,
            defaultValue: 0
          }
        }
      },
      defaultUnit: 'default'
    }
  };
  return res;
};

export const parseRunConfig = (zoneData, t, unit, criticalPower) => {
  const targetConfig = parseTargetConfig(t, unit);
  const allIntensityConfig = parseAllIntensityConfig(zoneData, unit, t, criticalPower);
  const targetTypeConfig = {
    distance: {
      ...targetConfig.distance,
      unitConfig: {
        km: targetConfig.distance.unitConfig.km,
        mi: targetConfig.distance.unitConfig.mi,
        m: targetConfig.distance.unitConfig.m
      }
    }
  };

  const intensity = {
    typeConfig: {},
    options: [
      ['%maxHr', '%rhr', '%lthr', 'rhr', '%thresholdPace', 'thresholdPace', '%adjustedPace', 'adjustedPace', 'power', 'cadence'],
      'notSet'
    ]
  };

  const result = {
    warmup: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['time', 'distance', 'load', 'manualEnd']
      },
      intensity
    },
    train: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['time', 'distance', 'load', 'manualEnd']
      },
      intensity
    },
    rest: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['time', 'distance', 'load', 'heartRateRecovery', 'manualEnd']
      },
      intensity
    },
    relax: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['time', 'distance', 'load', 'manualEnd']
      },
      intensity
    }
  };
  const config = exerciseConfigAnalyse(result, targetConfig, allIntensityConfig);
  return config;
};
/**
 * 越野跑
 */
export const parseTrailRunConfig = (zoneData, t, unit, criticalPower) => {
  const targetConfig = parseTargetConfig(t, unit);
  const allIntensityConfig = parseAllIntensityConfig(zoneData, unit, t, criticalPower);
  const targetTypeConfig = {
    distance: {
      ...targetConfig.distance,
      unitConfig: {
        km: targetConfig.distance.unitConfig.km,
        mi: targetConfig.distance.unitConfig.mi,
        m: targetConfig.distance.unitConfig.m
      }
    }
  };

  const intensity = {
    typeConfig: {},
    options: [
      ['%maxHr', '%rhr', '%lthr', 'rhr', '%thresholdPace', 'thresholdPace', '%adjustedPace', 'adjustedPace', 'power', 'cadence'],
      'notSet'
    ]
  };

  const result = {
    warmup: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['time', 'distance', 'load', 'cumulativeClimb', 'manualEnd']
      },
      intensity
    },
    train: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['time', 'distance', 'load', 'cumulativeClimb', 'manualEnd']
      },
      intensity
    },
    rest: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['time', 'distance', 'load', 'cumulativeClimb', 'heartRateRecovery', 'manualEnd']
      },
      intensity
    },
    relax: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['time', 'distance', 'load', 'cumulativeClimb', 'manualEnd']
      },
      intensity
    }
  };
  return exerciseConfigAnalyse(result, targetConfig, allIntensityConfig);
};
export const parseBikeConfig = (zoneData, t, unit, criticalPower) => {
  const targetConfig = parseTargetConfig(t, unit);
  const allIntensityConfig = parseAllIntensityConfig(zoneData, unit, t, criticalPower);
  const targetTypeConfig = {
    distance: {
      ...targetConfig.distance,
      unitConfig: {
        km: {
          ...targetConfig.distance.unitConfig.km,
          inputConfig: {
            ...targetConfig.distance.unitConfig.km.inputConfig,
            max: 499.99
          }
        },
        mi: {
          ...targetConfig.distance.unitConfig.mi,
          inputConfig: {
            ...targetConfig.distance.unitConfig.mi.inputConfig,
            max: 499.99
          }
        },
        m: {
          ...targetConfig.distance.unitConfig.m
        }
      }
    }
  };

  const intensity = {
    typeConfig: {
      power: {
        ...allIntensityConfig.power,
        unitConfig: {
          default: {
            ...allIntensityConfig.power.unitConfig.default,
            rangeConfig: {
              max: 2000,
              min: 10,
              precision: 0,
              step: 5,
              defaultValue: [200, 320],
              unit: ''
            }
          }
        }
      },
      cadence: {
        ...allIntensityConfig.cadence,
        // 踏频
        nameText: t('S3528'),
        unitConfig: {
          default: {
            typeConfig: null,
            rangeConfig: {
              max: 300,
              min: 30,
              precision: 0,
              step: 1,
              defaultValue: [70, 80],
              unit: ''
            }
          }
        }
      }
    },
    options: [['%maxHr', '%rhr', '%lthr', 'rhr', '%ftp', 'speed', 'power', 'cadence'], 'notSet']
  };

  const result = {
    warmup: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['time', 'distance', 'load', 'manualEnd']
      },
      intensity
    },
    train: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['time', 'distance', 'load', 'manualEnd']
      },
      intensity
    },
    rest: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['time', 'distance', 'load', 'heartRateRecovery', 'manualEnd']
      },
      intensity
    },
    relax: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['time', 'distance', 'load', 'manualEnd']
      },
      intensity
    }
  };
  return exerciseConfigAnalyse(result, targetConfig, allIntensityConfig);
};
export const parseSwimConfig = (zoneData, t, unit, criticalPower) => {
  const targetConfig = parseTargetConfig(t, unit);
  const allIntensityConfig = parseAllIntensityConfig(zoneData, unit, t, criticalPower);
  const targetTypeConfig = {
    distance: {
      ...targetConfig.distance,
      unitConfig: {
        m: {
          ...targetConfig.distance.unitConfig.m,
          inputConfig: {
            ...targetConfig.distance.unitConfig.m.inputConfig,
            step: 5
          }
        },
        yd: {
          ...targetConfig.distance.unitConfig.yd,
          inputConfig: {
            ...targetConfig.distance.unitConfig.yd.inputConfig,
            step: 5
          }
        }
      },
      defaultUnit: {
        [Definition.METRIC_UNITS]: 'm',
        [Definition.IMPERIAL_UNITS]: 'yd'
      }[unit]
    }
  };

  const intensity = {
    typeConfig: {},
    options: [['swimmingStyle'], 'notSet']
  };

  const result = {
    warmup: {
      target: {
        typeConfig: targetTypeConfig,
        options: [
          // 距离
          'distance',
          // 时间
          'time',
          'load',
          'manualEnd'
        ]
      },
      intensity
    },
    train: {
      target: {
        typeConfig: targetTypeConfig,
        options: [
          // 距离
          'distance',
          // 时间
          'time',
          'load',
          'manualEnd'
        ]
      },
      intensity
    },
    // 包干训练
    package: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['distance']
      },
      intensity
    },
    rest: {
      target: {
        typeConfig: targetTypeConfig,
        options: [
          // 距离
          'distance',
          // 时间
          'time',
          // 心率恢复
          'heartRateRecovery',
          'load',
          'manualEnd'
        ]
      },
      intensity
    },
    relax: {
      target: {
        typeConfig: targetTypeConfig,
        options: [
          // 距离
          'distance',
          // 时间
          'time',
          'load',
          'manualEnd'
        ]
      },
      intensity
    }
  };
  return exerciseConfigAnalyse(result, targetConfig, allIntensityConfig);
};
export const parseStrengthConfig = (zoneData, t, unit, criticalPower) => {
  const targetConfig = parseTargetConfig(t, unit);
  const allIntensityConfig = parseAllIntensityConfig(zoneData, unit, t, criticalPower);
  const targetTypeConfig = {
    // 时间
    time: {
      ...targetConfig.time,
      unitConfig: {
        default: {
          typeConfig: null,
          inputConfig: {
            max: 86399,
            min: 0,
            defaultValue: 30
          }
        }
      },
      defaultUnit: 'default'
    }
  };

  const intensityConfig = {
    notSet: allIntensityConfig.notSet,
    weight: allIntensityConfig.weight
  };

  const result = {
    warmup: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['time', 'manualEnd']
      },
      intensity: {
        typeConfig: {},
        options: []
      }
    },
    train: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['count', 'time', 'manualEnd']
      },
      intensity: {
        typeConfig: intensityConfig,
        options: ['weight']
      },
      sets: {
        typeConfig: {
          // 组数
          sets: {
            nameText: t('H6013'),
            unitConfig: {
              default: {
                typeConfig: null,
                inputConfig: {
                  max: 99,
                  min: 1,
                  precision: 0,
                  step: 1,
                  defaultValue: 1
                }
              }
            }
          }
        },
        options: ['sets']
      },
      rest: {
        typeConfig: {
          // 不休息
          noRest: {
            nameText: t('S4274')
          },
          // 休息（时间）
          time: {
            nameText: t('H6035'),
            unitConfig: {
              default: {
                typeConfig: null,
                inputConfig: {
                  max: 86399,
                  min: 0,
                  defaultValue: 60
                }
              }
            },
            defaultUnit: 'default'
          }
        },
        options: ['noRest', 'time']
      }
    },
    rest: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['time', 'heartRateRecovery', 'manualEnd']
      },
      intensity: {
        typeConfig: {},
        options: []
      }
    },
    relax: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['time', 'manualEnd']
      },
      intensity: {
        typeConfig: {},
        options: []
      }
    }
  };

  return exerciseConfigAnalyse(result, targetConfig, allIntensityConfig);
};
/**
 * 室内攀岩、抱石共用
 */
export const parseIndoorClimbConfig = (zoneData, t, unit, criticalPower, extraArgs) => {
  const targetConfig = parseTargetConfig(t, unit);
  const allIntensityConfig = parseAllIntensityConfig(zoneData, unit, t, criticalPower);
  const targetTypeConfig = {
    routes: { ...targetConfig.routes },
    time: {
      ...targetConfig.time,
      unitConfig: {
        default: {
          typeConfig: null,
          inputConfig: {
            max: 86399,
            min: 0,
            defaultValue: 30
          }
        }
      },
      defaultUnit: 'default'
    }
  };

  // 室内攀岩、抱石唯一不同的地方
  const intensity = {
    typeConfig: {
      gradeSystem: {
        ...allIntensityConfig.gradeSystem,
        unitConfig: {
          default: {
            ...allIntensityConfig.gradeSystem.unitConfig.default,
            typeConfig: {
              options: getGradingSytemOptions(extraArgs.sportType),
              // 这里的默认值会被 dealExerciseData 里面的覆盖
              defaultValue: 1
            }
          }
        }
      }
    },
    options: ['gradeSystem']
  };

  const result = {
    warmup: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['routes', 'time', 'manualEnd']
      },
      intensity
    },
    train: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['routes', 'time', 'manualEnd']
      },
      intensity
    },
    rest: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['routes', 'time', 'manualEnd']
      },
      intensity
    },
    relax: {
      target: {
        typeConfig: targetTypeConfig,
        options: ['routes', 'time', 'manualEnd']
      },
      intensity
    }
  };
  return exerciseConfigAnalyse(result, targetConfig, allIntensityConfig);
};

const sportConfigParsers = {
  run: parseRunConfig,
  bike: parseBikeConfig,
  swim: parseSwimConfig,
  strength: parseStrengthConfig,
  trailRun: parseTrailRunConfig,
  indoorClimb: parseIndoorClimbConfig,
  bouldering: parseIndoorClimbConfig
};

export const parseSportConfig = (sportType, zoneData, t, unit, criticalPower) => {
  const extraArgs = {
    sportType
  };
  const config = sportConfigParsers[Definition.sportCategoryName[sportType]](zoneData, t, unit, criticalPower, extraArgs);
  return config;
};
